import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
  timelineItemClasses,
} from "@mui/lab"
import { Typography, styled } from "@mui/material"
import { Check, HelpCircle } from "react-feather"

export interface LaboratoryTimelineProps {
  logo: ContentProps
  address: ContentProps
  phone: ContentProps
  web: ContentProps
  csContact: ContentProps
  crisisContact: ContentProps
}

interface LaboratoryTimelineContentProps {
  title: string
  content: ContentProps
  noWrap?: boolean
}

type ContentProps = (string | undefined)[]

export const LaboratoryTimeline = ({
  logo,
  address,
  phone,
  web,
  csContact,
  crisisContact,
}: LaboratoryTimelineProps) => {
  return (
    <Timeline
      sx={{
        [`& .${timelineItemClasses.root}:before`]: {
          flex: 0,
          padding: 0,
        },
        marginY: -1,
      }}
    >
      <LaboratoryTimelineItem title="Logo" content={logo} placement="first" />
      <LaboratoryTimelineItem title="Adresse" content={address} noWrap={false} />
      <LaboratoryTimelineItem title="Téléphone" content={phone} />
      <LaboratoryTimelineItem title="Site web" content={web} />
      <LaboratoryTimelineItem title="Contact SAV" content={csContact} />
      <LaboratoryTimelineItem
        title="Contact Temps de crise"
        content={crisisContact}
        placement="last"
      />
    </Timeline>
  )
}

const connectorMargin = 0.2

const LaboratoryTimelineItem = ({
  title,
  content,
  placement,
  noWrap,
}: LaboratoryTimelineContentProps & {
  placement?: "first" | "last"
}) => {
  const filled = !!content.filter(Boolean).length
  return (
    <TimelineItem>
      <TimelineSeparator>
        <Connector
          sx={{
            opacity: placement === "first" ? 0 : undefined,
            marginBottom: filled ? connectorMargin : undefined,
          }}
        />
        <LaboratoryTimelineDot isFilled={filled} />
        <Connector
          sx={{
            opacity: placement === "last" ? 0 : undefined,
            marginTop: filled ? connectorMargin : undefined,
          }}
        />
      </TimelineSeparator>
      <LaboratoryTimelineContent title={title} content={content} noWrap={noWrap} />
    </TimelineItem>
  )
}

const Content = styled(TimelineContent)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  paddingBottom: 0,
  paddingTop: 0,
  maxWidth: "100%",
})
const LaboratoryTimelineContent = ({
  title,
  content,
  noWrap = true,
}: LaboratoryTimelineContentProps) => {
  const hasContent = !!content.filter(Boolean).length
  const finalContent = hasContent ? content : [title]

  return (
    <Content>
      {hasContent && (
        <Typography
          component="span"
          fontSize={(theme) => theme.typography.pxToRem(12)}
          fontWeight="600"
          color="grey.light"
        >
          {title}
        </Typography>
      )}
      {finalContent.map((value, i) => (
        <Typography
          // biome-ignore lint/suspicious/noArrayIndexKey: static items
          key={i}
          fontSize={(theme) => theme.typography.pxToRem(14)}
          fontWeight={500}
          noWrap={noWrap}
        >
          {value}
        </Typography>
      ))}
    </Content>
  )
}

const Dot = styled(TimelineDot)({
  boxShadow: "none",
  margin: 0,
})
Dot.defaultProps = {
  color: "secondary",
}
const LaboratoryTimelineDot = ({ isFilled }: { isFilled: boolean }) => {
  return isFilled ? (
    <Dot color="secondary">
      <Check size={18} />
    </Dot>
  ) : (
    <Dot
      sx={(theme) => ({
        backgroundColor: "white",
        border: 0,
        color: theme.palette.grey.light,
        padding: 0,
      })}
    >
      <HelpCircle size={30} />
    </Dot>
  )
}

const Connector = styled(TimelineConnector)(({ theme }) => ({
  backgroundColor: theme.palette.grey.light,
  width: 1,
}))
